import React from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { formatMoney } from '@/utils/format';
import { EST3_QUOTE_QUALITIES } from './est3SubcontractKinds';

const { Text } = Typography;

export interface EpcEstimateSubcontractQuotePanelProps {
  estimateId: string;
  revisionId: string;
  packageId: string | null;
  packageRow?: Record<string, unknown> | null;
  readOnly?: boolean;
  onChanged?: () => void;
  compact?: boolean;
}

/**
 * EST3 UX2 Wave 6 — Subcontract quote refs (soft-link SQ/BA, select, coverage sync).
 */
export const EpcEstimateSubcontractQuotePanel: React.FC<EpcEstimateSubcontractQuotePanelProps> = ({
  estimateId,
  revisionId,
  packageId,
  packageRow,
  readOnly,
  onChanged,
  compact,
}) => {
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const [patchForm] = Form.useForm();
  const [quoteSearch, setQuoteSearch] = React.useState('');

  const quotesQ = useQuery({
    queryKey: ['epc-est-sub-quotes', estimateId, packageId],
    queryFn: () => epcEstimationService.listSubcontractQuoteRefs(estimateId, packageId!),
    enabled: !!estimateId && !!packageId,
  });

  const supplierQuotesQ = useQuery({
    queryKey: ['epc-est-sq-search', estimateId, quoteSearch],
    queryFn: () => epcEstimationService.searchSupplierQuotes(estimateId, quoteSearch || undefined),
    enabled: !!estimateId && !!packageId,
  });

  const bidAnalysesQ = useQuery({
    queryKey: ['epc-est-ba-search', estimateId],
    queryFn: () => epcEstimationService.searchBidAnalyses(estimateId),
    enabled: !!estimateId && !!packageId,
  });

  const addQuoteM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.addSubcontractQuoteRef(estimateId, packageId!, { revisionId, ...v }),
    onSuccess: () => {
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-est-sub-quotes', estimateId, packageId] });
      qc.invalidateQueries({ queryKey: ['epc-est-subpkg', estimateId, revisionId] });
      qc.invalidateQueries({ queryKey: ['epc-est-subcov', estimateId, revisionId] });
      qc.invalidateQueries({ queryKey: ['epc-est-cost', estimateId, revisionId] });
      onChanged?.();
    },
  });

  const selectQuoteM = useMutation({
    mutationFn: (quoteRefId: string) =>
      epcEstimationService.selectSubcontractQuote(estimateId, packageId!, quoteRefId, revisionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['epc-est-sub-quotes', estimateId, packageId] });
      qc.invalidateQueries({ queryKey: ['epc-est-subpkg', estimateId, revisionId] });
      qc.invalidateQueries({ queryKey: ['epc-est-subcov', estimateId, revisionId] });
      qc.invalidateQueries({ queryKey: ['epc-est-cost', estimateId, revisionId] });
      onChanged?.();
    },
  });

  const patchPkgM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.patchSubcontractPackage(estimateId, packageId!, { revisionId, ...v }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['epc-est-subpkg', estimateId, revisionId] });
      qc.invalidateQueries({ queryKey: ['epc-est-subcov', estimateId, revisionId] });
      onChanged?.();
    },
  });

  if (!packageId) {
    return <Empty description="Select a subcontract package to manage quote coverage." />;
  }

  const quotes = quotesQ.data || [];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {packageRow ? (
        <Descriptions size="small" column={compact ? 1 : 2} bordered>
          <Descriptions.Item label="Package">
            {String(packageRow.code || '—')} — {String(packageRow.name || '—')}
          </Descriptions.Item>
          <Descriptions.Item label="Coverage">
            <Tag color={packageRow.coverage_status === 'FIRM' ? 'green' : 'orange'}>
              {String(packageRow.coverage_status || 'UNQUOTED')}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Estimated amount">
            {formatMoney(packageRow.estimated_amount, { display: 'plain', maximumFractionDigits: 2 })}
          </Descriptions.Item>
          <Descriptions.Item label="Quotes">{quotes.length}</Descriptions.Item>
        </Descriptions>
      ) : null}

      {!readOnly ? (
        <Card size="small" title="Package adjustments">
          <Form
            key={`patch-${packageId}`}
            form={patchForm}
            layout="inline"
            initialValues={{
              allowanceAmount: packageRow?.allowance_amount ?? 0,
              riskLoadingPct: packageRow?.risk_loading_pct ?? 0,
            }}
            onFinish={(v) => patchPkgM.mutate(v)}
          >
            <Form.Item name="allowanceAmount" label="Allowance">
              <InputNumber min={0} />
            </Form.Item>
            <Form.Item name="riskLoadingPct" label="Risk %">
              <InputNumber min={0} max={100} />
            </Form.Item>
            <Button htmlType="submit" loading={patchPkgM.isPending}>
              Update
            </Button>
          </Form>
        </Card>
      ) : null}

      {!readOnly ? (
        <Card size="small" title="Add quote reference">
          <Form
            form={form}
            layout="vertical"
            onFinish={(v) => addQuoteM.mutate(v)}
            initialValues={{ quoteQuality: 'FIRM' }}
          >
            <Form.Item label="Search procurement quotes">
              <Input.Search
                allowClear
                placeholder="SQ number or supplier"
                onSearch={(v) => setQuoteSearch(v)}
                enterButton
              />
            </Form.Item>
            <Form.Item name="supplierQuotationId" label="Procurement supplier quote">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Soft-link existing SQ"
                options={(supplierQuotesQ.data || []).map((q: any) => ({
                  value: q.id,
                  label: `${q.quotation_number} · ${q.supplier_name || '—'} · ${formatMoney(q.amount, { display: 'plain', maximumFractionDigits: 0 })}${
                    q.validity_warning ? ' (expired)' : ''
                  }`,
                }))}
              />
            </Form.Item>
            <Form.Item name="bidAnalysisId" label="Bid analysis (optional)">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Link BA reference"
                options={(bidAnalysesQ.data || []).map((b: any) => ({
                  value: b.id,
                  label: `${b.analysis_number} · ${b.inquiry_number || b.status}`,
                }))}
              />
            </Form.Item>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="quoteQuality" label="Quality">
                  <Select options={EST3_QUOTE_QUALITIES.map((v) => ({ value: v, label: v }))} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="amount" label="Manual amount">
                  <InputNumber style={{ width: '100%' }} min={0} placeholder="If no SQ" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="supplierName" label="Supplier label">
              <Input />
            </Form.Item>
            <Form.Item name="validityDate" label="Validity (YYYY-MM-DD)">
              <Input placeholder="2026-12-31" />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={addQuoteM.isPending} block>
              Add quote reference
            </Button>
          </Form>
        </Card>
      ) : null}

      <Card size="small" title={`Quote references (${quotes.length})`}>
        <Table
          size="small"
          rowKey="id"
          loading={quotesQ.isLoading}
          dataSource={quotes}
          pagination={quotes.length > 15 ? { pageSize: 15 } : false}
          scroll={{ x: true }}
          columns={[
            { title: 'SQ', dataIndex: 'quotation_number', width: 110, ellipsis: true },
            { title: 'Supplier', dataIndex: 'supplier_name', ellipsis: true },
            {
              title: 'Quality',
              dataIndex: 'quote_quality',
              width: 100,
              render: (v) => <Tag>{v}</Tag>,
            },
            {
              title: 'Amount',
              dataIndex: 'amount',
              width: 100,
              render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 2 }),
            },
            {
              title: 'Valid',
              width: 90,
              render: (_: unknown, row: any) =>
                row.validity_warning ? (
                  <Tag color="red">Expired</Tag>
                ) : (
                  row.validity_date || '—'
                ),
            },
            {
              title: '',
              width: 100,
              render: (_: unknown, row: any) =>
                row.is_selected ? (
                  <Tag color="green">Selected</Tag>
                ) : readOnly ? null : (
                  <Button
                    size="small"
                    loading={selectQuoteM.isPending}
                    onClick={() => selectQuoteM.mutate(row.id)}
                  >
                    Select
                  </Button>
                ),
            },
          ]}
        />
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          Selecting a quote updates package coverage and syncs the linked SUBCONTRACT cost line.
        </Text>
      </Card>
    </Space>
  );
};
